import { useQuery } from '@tanstack/react-query';

const cache = new Map<string, unknown>();

interface ScryfallCardLite {
  name: string;
  mana_cost?: string;
  type_line?: string;
  oracle_text?: string;
  rarity?: string;
  colors?: ('W' | 'U' | 'B' | 'R' | 'G' | 'C')[];
  image_uris?: { small?: string; normal?: string; large?: string; art_crop?: string };
  card_faces?: { image_uris?: { small?: string; normal?: string; art_crop?: string } }[];
}

/**
 * Fetch card data by name. Tries Scryfall fuzzy match directly — Scryfall
 * is open & CORS-enabled, so this is fine to call from the browser. We
 * cache in-memory for the session.
 */
export function useCardLookup(name: string, enabled: boolean) {
  return useQuery({
    queryKey: ['card', name.toLowerCase()],
    enabled: enabled && name.length > 0,
    staleTime: Infinity,
    queryFn: async () => {
      const lower = name.toLowerCase();
      if (cache.has(lower)) return cache.get(lower) as ScryfallCardLite;
      const url = `https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(name)}`;
      const resp = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!resp.ok) throw new Error(`Scryfall ${resp.status}`);
      const raw = (await resp.json()) as ScryfallCardLite;
      const imageUris = raw.image_uris ?? raw.card_faces?.[0]?.image_uris;
      const normalized: ScryfallCardLite = {
        ...raw,
        ...(imageUris !== undefined ? { image_uris: imageUris } : {}),
      };
      cache.set(lower, normalized);
      return normalized;
    },
  });
}
